// Copyright (c) 2021, DIALPAD INC., all rights reserved.

import React, {Fragment} from 'react';
import {
    change,
    Field,
    FieldArray,
    focus,
    formValueSelector,
    reduxForm,
    reset,
} from 'redux-form';
import {find, forOwn, get, merge} from 'lodash';
import cx from 'classnames';
import {Prompt, withRouter} from 'react-router';
import ScrollBar from 'react-perfect-scrollbar';
import {connect} from 'react-redux';

import {RESPONSE_FORM} from '../../constants';
import {redux} from '@app/redux';
import {FlagIcon, KareError, QueriesInputError, TagsInput} from '@app/common';
import {blockUtils, formatTags, normalizeTags, filterFields} from './utils';
import {locale as localeUtil, querystring} from '@app/utils';
import {Add, ChatBubble, Check, Close, Create, Trash} from '@app/common/icons';
import {TitleInput} from './title-input';
import {RESPONSE_FORM_SOURCE} from '@app/redux/improve/responses/constants';
import {DocumentsModal} from '@app/improve/documents-modal';
import {selectDocumentAction} from '@app/redux/improve/documents/actions';
import {RESET_SELECT_DOCUMENT} from '@app/redux/improve/documents/constants';
import {GET_ATTACHED_DOCUMENT_FIELD_DOCUMENT} from '@app/redux/documents/constants';
import {
    BLOCK_TYPES,
    NODE_TYPES,
    CX_BLOCK_TYPES,
    BLOCK_KEYS,
} from '@app/constants';
import Modal from 'react-modal';
import {MODAL_BASE_CONFIG} from '@app/utils/modals';
import {MdClose} from 'react-icons/md';
import {DialogCreationModal} from './dialog-creation-modal';
import {ADD_QUOTE, REMOVE_QUOTE} from '@app/redux/dialogs/constants';
import {dialogQuoteSelector} from '@app/redux/dialogs/selectors';
import {getSelectedDocumentSelector} from '@app/redux/improve/documents/selectors';
import {documentSelector} from '@app/redux/documents/selectors';
import {
    DialogBlockField,
    ImageBlockField,
    QuoteBlockField,
    TextBlockField,
    VideoBlockField,
    HandoverBlockField,
    CTABlockField,
    NPSBlockField,
    CSATBlockField,
    CESBlockField,
    ExtensionBlockField,
    AttachmentsBlockField,
} from './block-fields';
import {AddBlockWidget} from './add-block-widget';
import {maxLength280, minLength5} from '@app/utils/validators';
import {ExtensionCreationModal} from './extension-creation-modal';
import {HandoverCreationModal} from './handover-creation-modal';
import {CTACreationModal} from './cta-creation-modal';
import {CXCreationModal} from './cx-creation-modal';
import {AttachmentCreationModal} from './attachment-creation-modal';
import {npsEnabled} from '@app/utils/dom';

// TODO(rupertrutland): extract this somewhere better.
const isRequired = (value) => (value ? undefined : 'Required');
const ACTIVATION_MODES = {
    queries: 'queries',
    parents: 'parents',
};

interface ResponseFormProps {
    location?: any;
    selectedDocument?: any;
    attachDocument?: any;
    setTitle?: any;
    currentQuery?: string;
    setEditorRef?: any;
    setQueries?: any;
    initialValues?: any;
    getRelatedResponses?: any;
    isCloning?: any;
    change?: any;
    quote?: any;
    document?: any;
    selectDocument?: any;
    resetForm?: any;
    resetFullDocument?: any;
    resetSelectedDocument?: any;
    queries?: any[];
    locale?: string;
    dialog?: any;
    outboundNodes?: any;
    responses?: any;
    blocksUtil?: any;
    removeDialog?: any;
    inboundNodes?: any;
    inboundNodesError?: any;
    inboundNodesLoading?: any;
    isChatteringTypeActive?: any;
    history?: any;
    blocks?: any;
    error?: any;
    linked_document?: any;
    isFormSubmitting?: any;
    dirty?: any;
    responseError?: any;
}

export class ResponseFormComponent extends React.Component<
    ResponseFormProps,
    any
> {
    // Base styling for all modals
    responseModalStyle = {
        ...MODAL_BASE_CONFIG,
        content: {
            ...MODAL_BASE_CONFIG.content,
            width: '750px',
            height: '660px',
            zIndex: '9999999',
        },
    };

    state = {
        showExtensionModal: false,
        selectionNode: null,
        openModal: false,
        showDialogModal: false,
        showHandoverModal: false,
        showAddBlockModal: false,
        showCTAModal: false,
        showAttachmentModal: false,
        showNPSModal: false,
        showCSATModal: false,
        showCESModal: false,
        activeChoiceBlockFormField: null,
        activation: ACTIVATION_MODES.queries,
        blocksFields: null,
        handoverBlockIndex: null,
        extensionBlockIndex: null,
        editing: false,
        dialogBlockIndex: null,
        quoteBlockIndex: null,
        isEditingQuoteBlock: false,
        editBlock: false,
        editCTABlock: false,
        editCSATBlock: false,
        csatBlockIndex: false,
        ctaBlockIndex: null,
        attachmentBlockIndex: null,
        editAttachmentBlock: false,
        npsBlockIndex: null,
        cesBlockIndex: null,
        editNPSBlock: false,
        editCESBlock: false,
    };
    markdownEditor = null;
    showMarkdownError = false;

    componentDidMount() {
        this.handlePreFilledFields();
        if (this.props.initialValues.id) {
            this.props.getRelatedResponses({id: this.props.initialValues.id});
        }

        if (this.props.isCloning) {
            this.props.change(
                'title',
                `Copy of ${this.props.initialValues.title}`,
            );
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.quote !== this.props.quote && !!this.props.quote) {
            const {id, external_url, title} = this.props.document;
            this.props.selectDocument(id, title, external_url);
        }

        if (!prevProps.isCloning && this.props.isCloning) {
            this.props.change(
                'title',
                `Copy of ${this.props.initialValues.title}`,
            );
        }
    }

    componentWillUnmount() {
        this.props.resetForm();
        this.props.resetFullDocument();
    }

    setSelectionNode = (node) => {
        this.setState({selectionNode: node});
    };

    closeModal = () => {
        this.setState({openModal: false, editing: false});
        this.props.resetSelectedDocument();
    };

    openModal = (editing = false) => {
        this.setState({openModal: true, editing});
    };

    renderDialog() {
        const {
            dialog,
            outboundNodes,
            responses,
            blocksUtil: {getBlocksWithNodeData},
        } = this.props;
        const choices = get(dialog, ['choices'], []);
        const blocks = [
            {
                choices_block: {
                    choices,
                },
                type: 'choices',
            },
        ];
        const richBlocks = getBlocksWithNodeData(blocks, [
            ...outboundNodes,
            ...responses,
        ]);
        const richChoiceBlock = richBlocks.find(
            (block) => block.type === 'choices',
        );
        const richChoices = get(richChoiceBlock, 'choices_block.choices', []);
        if (richChoices && richChoices.length > 0) {
            return (
                richChoices && (
                    <div className={'response-form__dialog'}>
                        <Close
                            className={'response-form__dialog__close'}
                            onClick={() => {
                                const confirm = window.confirm(
                                    'Are you sure you want to remove the dialog?',
                                );
                                if (confirm) {
                                    this.props.removeDialog();
                                }
                            }}
                        />
                        <h2>
                            <ChatBubble size={12} />
                            <span>Dialog Options</span>
                        </h2>
                        {richChoices.map((choice) => {
                            const text = get(choice, ['text'], '');
                            const title = get(choice, ['title'], text);
                            return (
                                <div
                                    key={choice.node}
                                    className={cx(
                                        'response-form__dialog-item',
                                        {
                                            edited: text.length > 0,
                                        },
                                    )}
                                >
                                    <ChatBubble size={10} />
                                    <div
                                        className={
                                            'response-form__dialog-item__text'
                                        }
                                    >
                                        <strong>{title}</strong>
                                        {text !== title ? (
                                            <small>{text}</small>
                                        ) : (
                                            <small>&nbsp;</small>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            );
        } else {
            return <div />;
        }
    }

    handlePreFilledFields() {
        const preFilledFields = [
            {id: 'title', handler: this.props.setTitle},
            {id: 'query', handler: this.props.setQueries},
        ];
        for (const field of preFilledFields) {
            const preFilledValue = querystring.getValueByKey(
                this.props.location.search,
                field.id,
            );
            if (preFilledValue) {
                field.handler(preFilledValue);
            }
        }
    }

    handleSubmitQuery = (value, key, fields) => {
        if (
            key === 'Enter' &&
            value &&
            value.length >= 2 &&
            value.length <= 280
        ) {
            this.props.change('queriesInput', '');
            fields.unshift(value);
        }
    };

    renderQueriesArray = ({fields, meta: {error, submitFailed}}) => {
        const {
            inboundNodes,
            inboundNodesError,
            inboundNodesLoading,
            isChatteringTypeActive,
        } = this.props;
        return (
            <Fragment>
                <div className="queries-list__activation">
                    <span>ACTIVATION</span>
                    <span
                        className={cx({
                            selected:
                                this.state.activation ===
                                ACTIVATION_MODES.queries,
                        })}
                        onClick={() => {
                            this.setState({
                                activation: ACTIVATION_MODES.queries,
                            });
                        }}
                    >
                        {fields && `${fields.length}`} Queries
                    </span>
                    <span
                        className={cx({
                            selected:
                                this.state.activation ===
                                ACTIVATION_MODES.parents,
                        })}
                        onClick={() => {
                            if (!inboundNodesError) {
                                this.setState({
                                    activation: ACTIVATION_MODES.parents,
                                });
                            }
                        }}
                    >
                        {inboundNodes &&
                        !inboundNodesError &&
                        !inboundNodesLoading
                            ? `${inboundNodes.length}`
                            : '0'}{' '}
                        Parents
                    </span>
                </div>
                {this.state.activation === ACTIVATION_MODES.queries ? (
                    <Fragment>
                        <div className="queries-list__input-container">
                            <Field
                                onKeyPress={(e) => {
                                    const value = e.target.value;
                                    const key = e.key;
                                    this.handleSubmitQuery(value, key, fields);
                                }}
                                name="queriesInput"
                                component={'input'}
                                placeholder="Add a query (between 2 and 280 characters)"
                                type="text"
                            />

                            <Add
                                size={18}
                                onClick={() => {
                                    this.handleSubmitQuery(
                                        this.props.currentQuery,
                                        'Enter',
                                        fields,
                                    );
                                }}
                            />
                        </div>
                        {(!fields || !fields.length) &&
                            submitFailed &&
                            isChatteringTypeActive && (
                                <QueriesInputError>{error}</QueriesInputError>
                            )}
                        <div className="queries-list__scroll">
                            {fields && fields.length > 0 ? (
                                <ScrollBar>
                                    {fields.map((field, index) => {
                                        return (
                                            <Field
                                                key={field}
                                                name={field}
                                                type="text"
                                                component={
                                                    this.renderQueryField
                                                }
                                                index={index}
                                                fields={fields}
                                            />
                                        );
                                    })}
                                </ScrollBar>
                            ) : (
                                <div className="queries-list__empty">
                                    <h1>What questions are you answering?</h1>
                                    <h2>
                                        Add customer queries that you want to
                                        respond to.
                                    </h2>
                                </div>
                            )}
                        </div>
                    </Fragment>
                ) : (
                    <div className="parents-list">
                        {inboundNodes &&
                            inboundNodes.length > 0 &&
                            inboundNodes.map((n) => {
                                const nodeId = get(n, ['node', 'id']);
                                return (
                                    <span
                                        onClick={() => {
                                            this.props.history.push(
                                                `/responses?compose=${nodeId}`,
                                            );
                                        }}
                                        key={nodeId}
                                    >
                                        {get(n, ['node', 'title'])}
                                    </span>
                                );
                            })}
                        {inboundNodes && inboundNodes.length === 0 && (
                            <div className="parents-list__empty">
                                <h1>
                                    {"This response doesn't have any parent."}
                                </h1>
                                <h2>
                                    Add your responses as dialog options to
                                    create dialog flows.
                                </h2>
                            </div>
                        )}
                    </div>
                )}
            </Fragment>
        );
    };

    hideAddBlockModal = () => {
        this.setState({
            showAddBlockModal: false,
            editBlock: false,
        });
    };

    addNewTextBlock = (fields, index) => () => {
        fields.insert(index, blockUtils.getNewTextBlock());
    };

    addNewImageBlock = (fields, index) => () => {
        fields.insert(index, blockUtils.getNewImageBlock());
    };

    addNewVideoBlock = (fields, index) => () => {
        fields.insert(index, blockUtils.getNewVideoBlock());
    };

    addNewExtensionBlock = (fields, index) => () => {
        this.setState({
            showExtensionModal: true,
            blocksFields: fields,
            extensionBlockIndex: index,
        });
    };

    addNewHandoverBlock = (fields, index) => () => {
        this.setState({
            showHandoverModal: true,
            blocksFields: fields,
            handoverBlockIndex: index,
        });
    };

    addNewQuoteBlock = (fields, index) => () => {
        this.setState({
            openModal: true,
            blocksFields: fields,
            quoteBlockIndex: index,
            isEditingQuoteBlock: false,
        });
    };

    addNewChoicesBlock = (fields, index) => () => {
        fields.insert(index, blockUtils.getNewChoicesBlock());
        this.setState({
            showDialogModal: true,
            blocksFields: fields,
            dialogBlockIndex: index,
        });
    };

    addNewCTABlock = (fields, index) => () => {
        this.setState({
            showCTAModal: true,
            blocksFields: fields,
            ctaBlockIndex: index,
        });
    };

    addNewAttachmentBlock = (fields, index) => () => {
        this.setState({
            showAttachmentModal: true,
            blocksFields: fields,
            attachmentBlockIndex: index,
        });
    };
    addNewNPSBlock = (fields, index) => () => {
        this.setState({
            showNPSModal: true,
            blocksFields: fields,
            npsBlockIndex: index,
        });
    };
    addNewCSATBlock = (fields, index) => () => {
        this.setState({
            showCSATModal: true,
            blocksFields: fields,
            csatBlockIndex: index,
        });
    };
    addNewCESBlock = (fields, index) => () => {
        this.setState({
            showCESModal: true,
            blocksFields: fields,
            cesBlockIndex: index,
        });
    };

    renderEditorArray = ({fields: actualFields}) => {
        const {blocks} = this.props;
        const fields = actualFields;
        const lastBlockClassName = cx('last-block-widget', {
            'last-block-widget--is-only-one': true,
        });
        filterFields(fields, npsEnabled());
        const numFields = fields.length;
        const formError = this.props.error;
        return (
            <div className="blocks-container">
                {!!formError && get(blocks, 'length', 0) === 0 && (
                    <div className="blocks-warning">
                        Forgot something? Add a message to publish your response
                    </div>
                )}
                {fields && numFields > 0 && (
                    <div>
                        {fields.map((field, index, fields) => {
                            const value = fields.get(index);
                            const id = get(value, ['id']);
                            const insertIndex = index + 1;
                            return (
                                <section
                                    key={field}
                                    className="array-field-editor-container"
                                >
                                    <Field
                                        key={id}
                                        name={field}
                                        component={this.renderEditorField}
                                        index={index}
                                        fields={fields}
                                    />
                                    {index < numFields - 1 && (
                                        <AddBlockWidget
                                            addNewExtensionBlock={this.addNewExtensionBlock(
                                                fields,
                                                numFields,
                                            )}
                                            addNewHandoverBlock={this.addNewHandoverBlock(
                                                fields,
                                                numFields,
                                            )}
                                            addNewTextBlock={this.addNewTextBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewImageBlock={this.addNewImageBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewVideoBlock={this.addNewVideoBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewQuoteBlock={this.addNewQuoteBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewChoicesBlock={this.addNewChoicesBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewCTABlock={this.addNewCTABlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewAttachmentBlock={this.addNewAttachmentBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewNPSBlock={this.addNewNPSBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewCSATBlock={this.addNewCSATBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            addNewCESBlock={this.addNewCESBlock(
                                                fields,
                                                insertIndex,
                                            )}
                                            hideComponent={
                                                this.hideAddBlockModal
                                            }
                                        />
                                    )}
                                </section>
                            );
                        })}
                    </div>
                )}
                <div className={lastBlockClassName}>
                    <AddBlockWidget
                        showSingleIcon={true}
                        addNewExtensionBlock={this.addNewExtensionBlock(
                            fields,
                            numFields,
                        )}
                        addNewTextBlock={this.addNewTextBlock(
                            fields,
                            numFields,
                        )}
                        addNewImageBlock={this.addNewImageBlock(
                            fields,
                            numFields,
                        )}
                        addNewVideoBlock={this.addNewVideoBlock(
                            fields,
                            numFields,
                        )}
                        addNewQuoteBlock={this.addNewQuoteBlock(
                            fields,
                            numFields,
                        )}
                        addNewHandoverBlock={this.addNewHandoverBlock(
                            fields,
                            numFields,
                        )}
                        addNewChoicesBlock={this.addNewChoicesBlock(
                            fields,
                            numFields,
                        )}
                        addNewCTABlock={this.addNewCTABlock(fields, numFields)}
                        addNewNPSBlock={this.addNewNPSBlock(fields, numFields)}
                        addNewCSATBlock={this.addNewCSATBlock(
                            fields,
                            numFields,
                        )}
                        addNewCESBlock={this.addNewCESBlock(fields, numFields)}
                        addNewAttachmentBlock={this.addNewAttachmentBlock(
                            fields,
                            numFields,
                        )}
                        hideComponent={this.hideAddBlockModal}
                    />
                </div>
            </div>
        );
    };

    renderQueryField = ({input, label, type, meta, index, fields}) => {
        const {name} = input;
        const {active} = meta;
        const className = cx('queries-list__container', {
            'queries-list__container--is-focused': active,
        });
        return (
            <div className={className}>
                <div className="queries-list__container-top">
                    <input
                        id={name}
                        className="queries-list__item"
                        {...input}
                        type={type}
                        placeholder={label}
                        title={get(input, 'value', '')}
                    />
                    <label htmlFor={name}>
                        <Create
                            size={16}
                            className="manage__query-list-item-remove"
                        />
                    </label>
                    <Trash
                        size={16}
                        className="manage__query-list-item-remove"
                        onClick={() => {
                            fields.remove(index);
                        }}
                    />
                </div>
                {active && (
                    <div className="queries-list__container-bottom">
                        <span>
                            DONE <Check size={10} />
                        </span>
                    </div>
                )}
            </div>
        );
    };

    renderEditorField = ({input, index, fields}) => {
        const blockType = get(input, ['value', 'type'], '');
        const removeBlock = () => {
            fields.remove(index);
        };
        if (blockType === 'handover') {
            return (
                <HandoverBlockField
                    input={input}
                    removeBlock={removeBlock}
                    index={index}
                    fields={fields}
                    activateBlock={() => {
                        this.setState({
                            showHandoverModal: true,
                            blocksFields: fields,
                            handoverBlockIndex: index,
                        });
                    }}
                />
            );
        }
        if (blockType === 'webext') {
            return (
                <ExtensionBlockField
                    input={input}
                    removeBlock={removeBlock}
                    index={index}
                    fields={fields}
                    activateBlock={() => {
                        this.setState({
                            showExtensionModal: true,
                            blocksFields: fields,
                            extensionBlockIndex: index,
                        });
                    }}
                />
            );
        }
        if (blockType === 'text') {
            return (
                <TextBlockField
                    input={input}
                    removeBlock={removeBlock}
                    index={index}
                    fields={fields}
                />
            );
        }
        if (blockType === 'image') {
            return (
                <ImageBlockField
                    input={input}
                    removeBlock={removeBlock}
                    index={index}
                    fields={fields}
                />
            );
        }
        if (blockType === 'video') {
            return (
                <VideoBlockField
                    input={input}
                    removeBlock={removeBlock}
                    index={index}
                    fields={fields}
                />
            );
        }
        if (blockType === BLOCK_TYPES.CTA) {
            return (
                <CTABlockField
                    input={input}
                    index={index}
                    fields={fields}
                    removeBlock={removeBlock}
                    activateBlock={() => {
                        this.setState({
                            showCTAModal: true,
                            blocksFields: fields,
                            ctaBlockIndex: index,
                            editCTABlock: true,
                        });
                    }}
                />
            );
        }
        if (blockType === BLOCK_TYPES.ATTACHMENTS) {
            return (
                <AttachmentsBlockField
                    input={input}
                    index={index}
                    fields={fields}
                    removeBlock={removeBlock}
                    activateBlock={() => {
                        this.setState({
                            showAttachmentModal: true,
                            blocksFields: fields,
                            attachmentBlockIndex: index,
                            editAttachmentBlock: true,
                        });
                    }}
                />
            );
        }
        if (blockType === 'quote') {
            return (
                <QuoteBlockField
                    input={input}
                    removeBlock={removeBlock}
                    activateBlock={() => {
                        this.setState({
                            openModal: true,
                            blocksFields: fields,
                            quoteBlockIndex: index,
                            isEditingQuoteBlock: true,
                        });
                    }}
                    index={index}
                    fields={fields}
                />
            );
        }
        if (blockType === 'choices') {
            //TODO Ask backend to sideload this data?
            const extendedInfoNodes = [];
            forOwn(get(input, ['value', 'choices_block', 'choices']), (c) => {
                const outboundNodes = get(this.props, 'outboundNodes').map(
                    (node) => node.node,
                );
                const foundNode = find(outboundNodes, {
                    id: c.node,
                });
                if (foundNode) {
                    extendedInfoNodes.push({...c, ...foundNode});
                } else {
                    extendedInfoNodes.push(c);
                }
            });
            if (!extendedInfoNodes || !extendedInfoNodes.length) {
                return null;
            }
            return (
                <DialogBlockField
                    choices={extendedInfoNodes}
                    input={input}
                    activateBlock={() => {
                        this.setState({
                            showDialogModal: true,
                            blocksFields: fields,
                            dialogBlockIndex: index,
                            editBlock: true,
                        });
                    }}
                    removeBlock={removeBlock}
                    index={index}
                    fields={fields}
                />
            );
        }

        if (blockType === BLOCK_TYPES.CX_QUESTION) {
            switch (input.value[BLOCK_KEYS.CX_QUESTION_BLOCK].type) {
                case CX_BLOCK_TYPES.NPS:
                    return (
                        <NPSBlockField
                            input={input}
                            index={index}
                            fields={fields}
                            removeBlock={removeBlock}
                            activateBlock={() => {
                                this.setState({
                                    showNPSModal: true,
                                    blocksFields: fields,
                                    npsBlockIndex: index,
                                    editNPSBlock: true,
                                });
                            }}
                        />
                    );
                case CX_BLOCK_TYPES.CSAT:
                    return (
                        <CSATBlockField
                            input={input}
                            index={index}
                            fields={fields}
                            removeBlock={removeBlock}
                            activateBlock={() => {
                                this.setState({
                                    showCSATModal: true,
                                    blocksFields: fields,
                                    csatBlockIndex: index,
                                    editCSATBlock: true,
                                });
                            }}
                        />
                    );
                case CX_BLOCK_TYPES.CES:
                    return (
                        <CESBlockField
                            input={input}
                            index={index}
                            fields={fields}
                            removeBlock={removeBlock}
                            activateBlock={() => {
                                this.setState({
                                    showCESModal: true,
                                    blocksFields: fields,
                                    cesBlockIndex: index,
                                    editCESBlock: true,
                                });
                            }}
                        />
                    );
                default:
                    return null;
            }
        }
        return null;
    };

    removeAttachment = () => {
        const isEmptySelection = !get(
            this.props.linked_document,
            ['selection'],
            false,
        );
        const confirm = !isEmptySelection
            ? window.confirm(
                  'Your response has an attached quote. Are you sure you want to remove it?',
              )
            : true;
        if (confirm) {
            this.props.resetFullDocument();
            this.props.change('linked_document', {id: null});
        }
    };

    //#region Hide modals

    hideDialogModal = () => {
        const {blocksFields} = this.state;

        this.setState({showDialogModal: false}, () => {
            this.state.blocksFields.forEach((f, i) => {
                const field = blocksFields.get(i);
                if (
                    field.type === 'choices' &&
                    field.choices_block.choices.length === 0
                ) {
                    blocksFields.remove(i);
                }
            });
        });
    };
    hideExtensionModal = () => {
        this.setState({showExtensionModal: false}, () => {});
    };

    hideCTAModal = () => {
        this.setState({showCTAModal: false}, () => {});
    };

    hideAttachmentModal = () => {
        this.setState({showAttachmentModal: false}, () => {});
    };

    hideHandoverCreationModal = () => {
        this.setState({showHandoverModal: false}, () => {});
    };

    hideNPSModal = () => {
        this.setState({showNPSModal: false}, () => {});
    };

    hideCSATModal = () => {
        this.setState({showCSATModal: false}, () => {});
    };

    hideCESModal = () => {
        this.setState({showCESModal: false}, () => {});
    };

    //#endregion Hide modals

    shouldComponentUpdate(nextProps) {
        return !(this.props.isFormSubmitting && !nextProps.isFormSubmitting);
    }

    // Merge 2 objects with a new output (so it doesn't mutate the target object)
    mergeStyles(obj1, obj2) {
        return merge({}, obj1, obj2);
    }

    render() {
        const {
            dirty,
            isFormSubmitting,
            responseError,
            locale,
            blocks,
            isChatteringTypeActive,
        } = this.props;

        if (responseError) {
            return <KareError />;
        }

        return (
            <div className="knowledge-base__response-form kare-center-block">
                {
                    //#region Handover Modal
                }
                <Modal
                    style={{
                        ...this.responseModalStyle,
                    }}
                    onRequestClose={() => {
                        this.setState({showHandoverModal: false});
                    }}
                    isOpen={this.state.showHandoverModal}
                >
                    <HandoverCreationModal
                        closeModal={this.hideHandoverCreationModal}
                        currentHandover={get(blocks, [
                            this.state.handoverBlockIndex,
                        ])}
                        addHandover={(payload) => {
                            const {blocksFields, handoverBlockIndex} =
                                this.state;
                            blocksFields.remove(handoverBlockIndex);
                            blocksFields.insert(handoverBlockIndex, payload);
                            this.setState({
                                blocksFields: blocksFields,
                            });
                        }}
                    />
                </Modal>
                {
                    //#endregion Handover Modal
                }
                {
                    //#region Extension Modal
                }
                <Modal
                    style={{
                        ...this.mergeStyles(this.responseModalStyle, {
                            content: {
                                width: '750px',
                                height: '660px',
                            },
                        }),
                    }}
                    onRequestClose={() => {
                        this.setState({showExtensionModal: false});
                    }}
                    isOpen={this.state.showExtensionModal}
                >
                    <ExtensionCreationModal
                        closeModal={this.hideExtensionModal}
                        currentExtension={get(blocks, [
                            this.state.extensionBlockIndex,
                        ])}
                        addExtension={(payload) => {
                            const {blocksFields, extensionBlockIndex} =
                                this.state;
                            blocksFields.remove(extensionBlockIndex);
                            blocksFields.insert(extensionBlockIndex, payload);
                            this.setState({
                                blocksFields: blocksFields,
                            });
                        }}
                    />
                </Modal>
                {
                    //#endregion Extension Modal
                }
                {
                    //#region Documents Modal
                }
                <DocumentsModal
                    showModal={this.state.openModal}
                    editing={this.state.editing}
                    closeModal={this.closeModal}
                    change={this.props.change}
                    fields={this.state.blocksFields}
                    index={this.state.quoteBlockIndex}
                    isEditing={this.state.isEditingQuoteBlock}
                />
                {
                    //#endregion Documents Modal
                }
                {
                    //#region Dialog Modal
                }
                <Modal
                    style={{
                        ...this.mergeStyles(this.responseModalStyle, {
                            content: {
                                width: '750px',
                                height: '750px',
                                maxHeight: '80vh',
                            },
                        }),
                    }}
                    onRequestClose={this.hideDialogModal}
                    shouldCloseOnOverlayClick={true}
                    isOpen={this.state.showDialogModal}
                >
                    <MdClose
                        className="close"
                        onClick={this.hideDialogModal}
                        size={20}
                    />
                    <DialogCreationModal
                        closeModal={this.hideDialogModal}
                        editingBlock={this.state.editBlock}
                        currentDialog={get(blocks, [
                            this.state.dialogBlockIndex,
                        ])}
                        addDialog={(payload) => {
                            const {blocksFields, dialogBlockIndex} = this.state;
                            if (this.state.editBlock) {
                                blocksFields.remove(dialogBlockIndex);
                            }
                            blocksFields.insert(dialogBlockIndex, payload);
                            this.setState({
                                blocksFields: blocksFields,
                            });
                        }}
                    />
                </Modal>
                {
                    //#endregion Dialog Modal
                }
                {
                    //#region Call to Action (CTA) Modal
                }
                <Modal
                    style={{
                        ...this.mergeStyles(this.responseModalStyle, {
                            content: {
                                width: '400px',
                                height: '333px',
                                maxHeight: '80vh',
                            },
                        }),
                    }}
                    onRequestClose={this.hideCTAModal}
                    shouldCloseOnOverlayClick={true}
                    isOpen={this.state.showCTAModal}
                >
                    <MdClose
                        className="close"
                        onClick={this.hideCTAModal}
                        size={20}
                    />
                    <CTACreationModal
                        closeModal={this.hideCTAModal}
                        currentCTA={get(blocks, [this.state.ctaBlockIndex])}
                        addCTA={(cta) => {
                            const {blocksFields, ctaBlockIndex, editCTABlock} =
                                this.state;
                            if (editCTABlock) {
                                blocksFields.remove(ctaBlockIndex);
                            }
                            blocksFields.insert(ctaBlockIndex, cta);
                            this.setState({
                                blocksFields: blocksFields,
                            });
                        }}
                    />
                </Modal>
                {
                    //#endregion Call to Action (CTA) Modal
                }
                {
                    //#region Attachment Modal
                }
                <Modal
                    style={{
                        ...this.mergeStyles(this.responseModalStyle, {
                            content: {
                                width: '400px',
                                height: '400px',
                                maxHeight: '80vh',
                            },
                        }),
                    }}
                    onRequestClose={this.hideAttachmentModal}
                    shouldCloseOnOverlayClick={true}
                    isOpen={this.state.showAttachmentModal}
                >
                    <MdClose
                        className="close"
                        onClick={this.hideAttachmentModal}
                        size={20}
                    />
                    <AttachmentCreationModal
                        closeModal={this.hideAttachmentModal}
                        currentAttachment={get(blocks, [
                            this.state.attachmentBlockIndex,
                        ])}
                        addAttachments={(attachments) => {
                            const {
                                blocksFields,
                                attachmentBlockIndex,
                                editAttachmentBlock,
                            } = this.state;
                            if (editAttachmentBlock) {
                                blocksFields.remove(attachmentBlockIndex);
                            }
                            blocksFields.insert(
                                attachmentBlockIndex,
                                attachments,
                            );
                            this.setState({
                                blocksFields: blocksFields,
                            });
                        }}
                    />
                </Modal>
                {
                    //#endregion Attachment Modal
                }
                {
                    //#region NPS Modal
                }
                <Modal
                    style={{
                        ...this.mergeStyles(this.responseModalStyle, {
                            content: {
                                height: '545px',
                                width: '400px',
                                maxHeight: '80vh',
                            },
                        }),
                    }}
                    onRequestClose={this.hideNPSModal}
                    shouldCloseOnOverlayClick={true}
                    isOpen={this.state.showNPSModal}
                >
                    <MdClose
                        className="close"
                        onClick={this.hideNPSModal}
                        size={20}
                    />
                    <CXCreationModal
                        closeModal={this.hideNPSModal}
                        currentCX={get(blocks, [this.state.npsBlockIndex])}
                        addCX={(nps) => {
                            const {blocksFields, npsBlockIndex, editNPSBlock} =
                                this.state;
                            if (editNPSBlock) {
                                blocksFields.remove(npsBlockIndex);
                            }
                            blocksFields.insert(npsBlockIndex, nps);
                            this.setState({
                                blocksFields: blocksFields,
                            });
                        }}
                        type={CX_BLOCK_TYPES.NPS}
                    />
                </Modal>
                {
                    //#endregion NPS Modal
                }
                {
                    //#region CSAT Modal
                }
                <Modal
                    style={{
                        ...this.mergeStyles(this.responseModalStyle, {
                            content: {
                                height: '615px',
                                width: '430px',
                                maxHeight: '80vh',
                            },
                        }),
                    }}
                    onRequestClose={this.hideCSATModal}
                    shouldCloseOnOverlayClick={true}
                    isOpen={this.state.showCSATModal}
                >
                    <MdClose
                        className="close"
                        onClick={this.hideCSATModal}
                        size={20}
                    />
                    <CXCreationModal
                        closeModal={this.hideCSATModal}
                        currentCX={get(blocks, [this.state.csatBlockIndex])}
                        addCX={(csat) => {
                            const {
                                blocksFields,
                                csatBlockIndex,
                                editCSATBlock,
                            } = this.state;
                            if (editCSATBlock) {
                                blocksFields.remove(csatBlockIndex);
                            }
                            blocksFields.insert(csatBlockIndex, csat);
                            this.setState({
                                blocksFields: blocksFields,
                            });
                        }}
                        type={CX_BLOCK_TYPES.CSAT}
                    />
                </Modal>
                {
                    //#endregion CSAT Modal
                }
                {
                    //#region CES Modal
                }
                <Modal
                    style={{
                        ...this.mergeStyles(this.responseModalStyle, {
                            content: {
                                height: '545px',
                                width: '400px',
                                maxHeight: '80vh',
                            },
                        }),
                    }}
                    onRequestClose={this.hideCESModal}
                    shouldCloseOnOverlayClick={true}
                    isOpen={this.state.showCESModal}
                >
                    <MdClose
                        className="close"
                        onClick={this.hideCESModal}
                        size={20}
                    />
                    <CXCreationModal
                        closeModal={this.hideCESModal}
                        currentCX={get(blocks, [this.state.cesBlockIndex])}
                        addCX={(ces) => {
                            const {blocksFields, cesBlockIndex, editCESBlock} =
                                this.state;
                            if (editCESBlock) {
                                blocksFields.remove(cesBlockIndex);
                            }
                            blocksFields.insert(cesBlockIndex, ces);
                            this.setState({
                                blocksFields: blocksFields,
                            });
                        }}
                        type={CX_BLOCK_TYPES.CES}
                    />
                </Modal>
                {
                    //#endregion CES Modal
                }
                <Prompt
                    message="Discard unsaved changes?"
                    when={dirty && !isFormSubmitting}
                />
                <form
                    className={cx(
                        'u-1/1',
                        'knowledge-base__response-form__inner-container',
                    )}
                >
                    <div
                        className={
                            'knowledge-base__response-form__main-fields-container'
                        }
                    >
                        <Field
                            name="title"
                            component={TitleInput}
                            placeholder="Title"
                            validate={[isRequired, maxLength280, minLength5]}
                            className={'knowledge-base__response-form__title'}
                            props={{maxLength: 280, minLength: 5}}
                        />
                        {!isChatteringTypeActive && (
                            <Field
                                name="labels"
                                component={TagsInput}
                                normalize={(tags) => normalizeTags(tags)}
                                format={(tags) => formatTags(tags)}
                            />
                        )}
                        <div style={{flexGrow: 2, overflowY: 'scroll'}}>
                            <FieldArray
                                name="blocks"
                                component={this.renderEditorArray}
                            />
                            {this.renderDialog()}
                        </div>
                        <div className="response-editor__attachment">
                            <Field
                                name={RESPONSE_FORM_SOURCE}
                                hidden={true}
                                component="input"
                            />
                            <span
                                className={'response-portal__locale'}
                                title={localeUtil.getLocaleName(locale)}
                            >
                                <FlagIcon
                                    code={localeUtil
                                        .getCountryCode(locale)
                                        .toLowerCase()}
                                />
                            </span>
                        </div>
                    </div>
                    <div
                        className={
                            'knowledge-base__response-form__queries-list'
                        }
                    >
                        {/* TODO(rupertrutland): separate this out into separate component */}
                        <FieldArray
                            name={`${
                                isChatteringTypeActive
                                    ? NODE_TYPES.CHATTER
                                    : NODE_TYPES.RESPONSE
                            }.queries`}
                            component={this.renderQueriesArray}
                            validate={
                                isChatteringTypeActive
                                    ? [
                                          (value) =>
                                              value && value.length === 0
                                                  ? 'Min one query'
                                                  : undefined,
                                      ]
                                    : []
                            }
                        />
                    </div>
                </form>
                {this.props.children}
            </div>
        );
    }
}

/**
 * Map redux store state to props for the component.
 * @param {Object} state
 * @param {Object} props
 */
const mapStateToProps = (state, props) => {
    return {
        fullDocument:
            redux.documents.selectors.attachedDocumentFieldDocumentSelector(
                state,
            ),
        queries: formValueSelector(RESPONSE_FORM)(state, 'queries'),
        currentQuery: formValueSelector(RESPONSE_FORM)(state, 'queriesInput'),
        response: formValueSelector(RESPONSE_FORM)(state, 'response'),
        isResponseFetching:
            redux.responses.selectors.isResponseFetchingSelector(state),
        inboundNodes: redux.knowledgeBase.selectors.inboundNodesSelector(state),
        inboundNodesError:
            redux.knowledgeBase.selectors.inboundNodesErrorSelector(state),
        inboundNodesLoading:
            redux.knowledgeBase.selectors.inboundNodesLoadingSelector(state),
        document: documentSelector(state),
        selectedDocument: getSelectedDocumentSelector(state),
        quote: dialogQuoteSelector(state),
        dialog: get(formValueSelector(RESPONSE_FORM)(state, 'choice'), [
            'choices_block',
        ]),
        choice: formValueSelector(RESPONSE_FORM)(state, 'choice'),
        blocks: formValueSelector(RESPONSE_FORM)(state, 'blocks'),
        isFormSubmitting:
            get(state, ['createResponse', 'loading'], false) ||
            get(state, ['createResponse', 'sync'], false) ||
            get(state, ['updateResponse', 'loading'], false) ||
            get(state, ['updateResponse', 'sync'], false) ||
            props.submitting,
        responseError: redux.responses.selectors.responseErrorSelector(state),
        responses:
            redux.knowledgeBase.selectors.publishedResponsesSelector(state),
        outboundNodes: redux.responses.selectors.outboundNodesSelector(state),
        locale: redux.locale.selectors.contentLocaleSelector(state),
    };
};

const mapDispatchToProps = (dispatch) => ({
    removeDialog: () => {
        dispatch(change(RESPONSE_FORM, 'choice', null));
    },
    addDialog: (dialogData) => {
        dispatch(change(RESPONSE_FORM, 'blocks', dialogData));
    },
    addQuote: (quoteData) => {
        dispatch({type: ADD_QUOTE, payload: quoteData});
    },
    getOutboundNodes: (id) => {
        return dispatch(
            redux.rest.actions[redux.responses.constants.GET_OUTBOUND_NODES]({
                id,
            }),
        );
    },
    selectDocument: (id, title, url) =>
        dispatch(selectDocumentAction(id, title, url)),
    resetSelectedDocument: () => dispatch({type: RESET_SELECT_DOCUMENT}),
    resetFullDocument: () => {
        dispatch(
            redux.rest.actions[GET_ATTACHED_DOCUMENT_FIELD_DOCUMENT].reset(),
        );
        dispatch({type: REMOVE_QUOTE});
    },
    attachDocument:
        redux.responses.dispatchers.attachDocumentDispatcher(dispatch),
    getRelatedResponses:
        redux.knowledgeBase.dispatchers.getAllInboundNodesDispatcher(dispatch),
    setTitle: redux.responses.dispatchers.setResponseTitleDispatcher(dispatch),
    setQueries:
        redux.responses.dispatchers.setResponseQueriesDispatcher(dispatch),
    resetForm: () => {
        dispatch(reset(RESPONSE_FORM));
    },
    focusField: (fieldName) => {
        dispatch(focus(RESPONSE_FORM, fieldName));
    },
});

export const ResponseForm = withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(
        reduxForm({
            form: RESPONSE_FORM,
            enableReinitialize: true,
        })(ResponseFormComponent),
    ),
);
